/**
 * PartiesPanel — slot-aware editor surface for contract parties
 * (per ADR-0018 + slice C-prefill).
 *
 * The template declares its party slots via `TemplateMetadata.partySlots`
 * (each slot a `(role, ordinal)` pair with a Swedish label + required
 * flag). This panel renders one card per slot at the top.
 *
 * Editing model: parties are buffered LOCALLY in the parent editor and
 * only persisted on explicit Save. The panel therefore never calls the
 * DB; it surfaces add/remove/update intents through props. The parent
 * owns the `LocalParty[]` list and the eventual save_draft payload.
 *
 * Classification (`privacy_class`) is computed from the input id_type +
 * id_number by the shared classifier so the panel can show the chip
 * before the row hits the server.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pencil,
  Trash2,
  Users,
  UserPlus,
  ShieldAlert,
  ShieldCheck,
  X,
} from 'lucide-react';
import {
  classifyParty,
  type PartyIdType as ClassifierIdType,
} from '@regta/common/classify-party';
import {
  RENDERERS,
  type PartySlot,
  type VariableField,
} from '@hyranai/regta-templates';
import {
  tenantApi,
  type PartyIdType,
  type PartyPrivacyClass,
  type PartyRole,
  type PartySearchHit,
} from '../api.js';
import { EntityCombobox, type EntityComboboxOption } from './EntityCombobox.js';
import { locateAnchor } from '../lib/locate-anchor.js';
import {
  EMPTY_PARTY_DRAFT,
  applyPartyDraft,
  draftFromParty,
  draftSnapshot,
  fillDraftFromLookup,
  type PartyDraft,
} from '../lib/party-draft.js';

const ROLE_LABEL: Record<PartyRole, string> = {
  lessor: 'Hyresvärd',
  lessee: 'Hyresgäst',
  guarantor: 'Borgensman',
  witness: 'Vittne',
  other: 'Annan',
};

const PRIVACY_LABEL: Record<PartyPrivacyClass, string> = {
  searchable_org: 'Org · sökbar',
  pii_org: 'Org · PII',
  pii_individual: 'PII',
  unknown: 'Ouppgivet',
};

const PRIVACY_CLASS: Record<PartyPrivacyClass, string> = {
  searchable_org: 'bg-green-50 text-green-800 border-green-200',
  pii_org: 'bg-amber-50 text-amber-800 border-amber-200',
  pii_individual: 'bg-amber-100 text-amber-900 border-amber-300',
  unknown: 'bg-stone-100 text-stone-700 border-stone-300',
};

const ID_TYPE_LABEL: Record<PartyIdType, string> = {
  org_no: 'Organisationsnr',
  person_no: 'Personnummer',
  foreign: 'Utländskt ID',
  none: 'Inget ID',
};

/**
 * Frontend-only party shape during editing. `_persistedId` is set when
 * the row was loaded from the server; absent rows are local-only adds.
 * Classification is recomputed any time id_type/id_number change.
 */
export type LocalParty = {
  _clientId: string;
  _persistedId?: string;
  role: PartyRole;
  ordinal: number;
  id_type: PartyIdType;
  id_number: string | null;
  legal_name: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  provenance?:
    | 'manual'
    | 'predefined'
    | 'self'
    | 'search'
    | 'bolagsverket'
    | 'extracted';
  privacy_class: PartyPrivacyClass;
  /** A persisted row edited locally via "Redigera" — sent as
   *  `save_draft.parties_update` on Save. */
  _dirty?: boolean;
};

export function PartiesPanel({
  contractId: _contractId,
  token,
  parties,
  values,
  slots,
  variableSchema,
  onAddParty,
  onRemoveParty,
  onUpdateParty,
  onFieldChange,
  disabled,
  sectionPrefix,
  excludeSlot,
}: {
  contractId: string;
  token: string;
  parties: LocalParty[];
  values: Record<string, unknown>;
  slots: ReadonlyArray<PartySlot>;
  variableSchema: ReadonlyArray<VariableField<Record<string, unknown>>>;
  onAddParty: (party: LocalParty) => void;
  onRemoveParty: (clientId: string) => void;
  /** Replace a seated party with its edited version ("Redigera"). Omit to
   *  hide the edit action. */
  onUpdateParty?: (clientId: string, next: LocalParty) => void;
  onFieldChange: (key: string, value: unknown) => void;
  disabled: boolean;
  /** "§1–2"-style prefix (per ADR-0049) showing which document sections
   *  the parties realise. Optional — legacy templates omit it. */
  sectionPrefix?: string;
  /** Predicate to drop specific slots from the top panel so the parent
   *  can embed them elsewhere (e.g. the guarantor slot rendered next to
   *  its `has_guarantor` checkbox via the Borgensman section). */
  excludeSlot?: (slot: PartySlot) => boolean;
}) {
  const visibleSlots = useMemo(
    () => (excludeSlot ? slots.filter((s) => !excludeSlot(s)) : slots),
    [slots, excludeSlot],
  );
  const slotEntries = useMemo(
    () =>
      visibleSlots.map((slot) => {
        const party =
          parties.find(
            (p) => p.role === slot.role && p.ordinal === slot.ordinal,
          ) ?? null;
        const fields = variableSchema.filter(
          (f) =>
            f.autofill?.kind === 'party' &&
            f.autofill.role === slot.role &&
            f.autofill.ordinal === slot.ordinal,
        );
        return { slot, party, fields };
      }),
    [visibleSlots, parties, variableSchema],
  );

  const offSlotParties = useMemo(() => {
    const declared = new Set(slots.map((s) => `${s.role}#${s.ordinal}`));
    return parties.filter((p) => !declared.has(`${p.role}#${p.ordinal}`));
  }, [slots, parties]);

  return (
    <fieldset className="flex flex-col gap-3 rounded border border-stone-200 p-3">
      <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
        <Users className="h-3.5 w-3.5" />
        {sectionPrefix && <span className="text-stone-400">{sectionPrefix}</span>}
        Avtalsparter
      </legend>

      <div className="space-y-3">
        {slotEntries.map(({ slot, party, fields }) => (
          <PartySlotCard
            key={`${slot.role}-${slot.ordinal}`}
            token={token}
            slot={slot}
            party={party}
            fields={fields}
            values={values}
            onAddParty={onAddParty}
            onRemoveParty={onRemoveParty}
            onUpdateParty={onUpdateParty}
            onFieldChange={onFieldChange}
            disabled={disabled}
          />
        ))}
      </div>

      {offSlotParties.length > 0 && (
        <div className="mt-2 border-t border-stone-200 pt-3">
          <div className="mb-2 text-[10px] font-medium uppercase tracking-widest text-stone-400">
            Andra parter
          </div>
          <ul className="space-y-1">
            {offSlotParties.map((p) => (
              <OffSlotRow
                key={p._clientId}
                party={p}
                onRemoveParty={onRemoveParty}
                disabled={disabled}
              />
            ))}
          </ul>
        </div>
      )}
    </fieldset>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Per-slot card
// ──────────────────────────────────────────────────────────────────────

function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

export function PartySlotCard({
  token,
  slot,
  party,
  fields,
  values,
  onAddParty,
  onRemoveParty,
  onUpdateParty,
  onFieldChange,
  disabled,
}: {
  token: string;
  slot: PartySlot;
  party: LocalParty | null;
  fields: ReadonlyArray<VariableField<Record<string, unknown>>>;
  values: Record<string, unknown>;
  onAddParty: (party: LocalParty) => void;
  onRemoveParty: (clientId: string) => void;
  /** Replace the seated party with its edited version. Omit to hide
   *  "Redigera". */
  onUpdateParty?: (clientId: string, next: LocalParty) => void;
  onFieldChange: (key: string, value: unknown) => void;
  disabled: boolean;
}) {
  // Lessor slots are "self-partyable" (per ADR-0018): the tenant's saved
  // identities are the expected pick, so the card opens straight into the
  // picker with those surfaced up-front — search/manual stay as fallbacks.
  const isSelfPartyable = slot.role === 'lessor';
  const initialMode = isSelfPartyable ? 'search' : 'idle';
  const [mode, setMode] = useState<'idle' | 'search' | 'manual'>(initialMode);
  const [draft, setDraft] = useState<PartyDraft>(EMPTY_PARTY_DRAFT);
  const [editDraft, setEditDraft] = useState<PartyDraft | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setDraft(EMPTY_PARTY_DRAFT);
    setMode(initialMode);
    setErr(null);
  };

  const submitManual = () => {
    const snap = draftSnapshot(draft);
    if (!snap.legal_name) {
      setErr('Namn krävs.');
      return;
    }
    const classification = classifyParty({
      id_type: snap.id_type as ClassifierIdType,
      id_number: snap.id_number,
    });
    onAddParty({
      _clientId: newClientId(),
      role: slot.role,
      ordinal: slot.ordinal,
      ...snap,
      display_name: null,
      provenance: 'manual',
      privacy_class: classification.privacy_class,
    });
    reset();
  };

  const startEdit = () => {
    if (!party) return;
    setErr(null);
    setEditDraft(draftFromParty(party));
  };

  const submitEdit = () => {
    if (!party || !editDraft || !onUpdateParty) return;
    if (!editDraft.legal_name.trim()) {
      setErr('Namn krävs.');
      return;
    }
    onUpdateParty(party._clientId, applyPartyDraft(party, editDraft));
    setEditDraft(null);
    setErr(null);
  };

  const pickFromSearch = useCallback(
    (hit: PartySearchHit) => {
      const classification = classifyParty({
        id_type: hit.idType as ClassifierIdType,
        id_number: hit.idNumber,
      });
      onAddParty({
        _clientId: newClientId(),
        role: slot.role,
        ordinal: slot.ordinal,
        id_type: hit.idType,
        id_number: hit.idNumber,
        legal_name: hit.legalName,
        display_name: null,
        email: hit.email,
        phone: hit.phone,
        address_line1: hit.addressLine1,
        address_line2: hit.addressLine2,
        postal_code: hit.postalCode,
        city: hit.city,
        country: hit.country,
        provenance:
          hit.source === 'self'
            ? 'self'
            : hit.source === 'predefined'
              ? 'predefined'
              : 'bolagsverket',
        privacy_class: classification.privacy_class,
      });
      reset();
    },
    [onAddParty, slot.role, slot.ordinal],
  );

  const search = useCallback(
    async (q: string): Promise<EntityComboboxOption<PartySearchHit>[]> => {
      const trimmed = q.trim();
      // For lessor slots, surface "Egen identitet" entries up-front so
      // the user doesn't have to type anything to find their own org.
      // Other roles still require a query to avoid dumping results.
      if (!trimmed && slot.role !== 'lessor') return [];
      const r = await tenantApi.parties.search({ q: trimmed, limit: 10 });
      return r.hits.map((h) => ({
        id: `${h.source}:${h.id}`,
        primary: h.legalName,
        secondary:
          [
            h.idNumber,
            h.source === 'self'
              ? 'Egen identitet'
              : h.source === 'predefined'
                ? 'Förvalt'
                : 'Bolagsverket',
          ]
            .filter(Boolean)
            .join(' · ') || null,
        value: h,
      }));
    },
    [token, slot.role],
  );

  const removeParty = () => {
    if (!party) return;
    setEditDraft(null);
    onRemoveParty(party._clientId);
  };

  const editing = party !== null && editDraft !== null && !disabled;

  return (
    <div
      data-locate-anchor={locateAnchor('slot', slot.id)}
      className="rounded border border-stone-200 bg-white p-3"
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-stone-700">
            {slot.label}
            {slot.required ? (
              <span className="ml-2 rounded bg-stone-100 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-stone-500">
                Obligatorisk
              </span>
            ) : (
              <span className="ml-2 rounded bg-stone-50 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-stone-400">
                Valfritt
              </span>
            )}
          </div>
          {slot.help && (
            <div className="mt-0.5 text-[11px] text-stone-500">{slot.help}</div>
          )}
        </div>
      </div>

      {err && (
        <div className="mb-2 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          {err}
        </div>
      )}

      {party ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 rounded bg-green-50 px-2 py-1.5 text-xs text-green-900">
            <span className="font-medium">{party.legal_name}</span>
            <PartyClassBadge cls={party.privacy_class} />
            <span className="text-green-700">
              · {ID_TYPE_LABEL[party.id_type]}
              {party.id_number ? ` ${party.id_number}` : ''}
            </span>
            {(!party._persistedId || party._dirty) && (
              <span className="ml-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-800">
                Osparad
              </span>
            )}
            {!disabled && !editing && (
              <span className="ml-auto inline-flex gap-1">
                {onUpdateParty && (
                  <button
                    type="button"
                    onClick={startEdit}
                    className="inline-flex items-center gap-1 rounded border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:bg-stone-50"
                    title="Ändra partens namn, ID, kontaktuppgifter och adress"
                  >
                    <Pencil className="h-3 w-3" />
                    Redigera
                  </button>
                )}
                <button
                  type="button"
                  onClick={removeParty}
                  className="inline-flex items-center gap-1 rounded border border-stone-300 bg-white px-2 py-0.5 text-[11px] font-medium text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                  title="Ångra valet — t.ex. om du råkat skriva fel"
                >
                  <X className="h-3 w-3" />
                  Avbryt
                </button>
              </span>
            )}
          </div>
          {!editing && <PartyContactSummary party={party} />}
          {editing && editDraft && (
            <PartyDraftForm
              draft={editDraft}
              onChange={setEditDraft}
              submitLabel="Spara ändringar"
              onSubmit={submitEdit}
              onCancel={() => {
                setEditDraft(null);
                setErr(null);
              }}
            />
          )}
          {fields.length > 0 && (
            <PrefilledFields
              fields={fields}
              values={values}
              partyName={party.legal_name}
              onFieldChange={onFieldChange}
              disabled={disabled}
            />
          )}
        </div>
      ) : disabled ? (
        <div className="rounded border border-dashed border-stone-300 p-3 text-center text-xs text-stone-500">
          Avtalet är inte i utkast — ingen part kopplad.
        </div>
      ) : mode === 'idle' ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMode('search')}
            className="inline-flex items-center gap-1.5 rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
          >
            <UserPlus className="h-3.5 w-3.5" />
            Sök i listor
          </button>
          <button
            type="button"
            onClick={() => setMode('manual')}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white"
          >
            Skriv in manuellt
          </button>
          {slot.id === 'property_assoc' && (
            <button
              type="button"
              onClick={() => {
                onAddParty({
                  _clientId: newClientId(),
                  role: slot.role,
                  ordinal: slot.ordinal,
                  id_type: 'org_no',
                  id_number: '8025562797',
                  legal_name: 'HYRESBOSTADSÄGARNA I STOCKHOLM',
                  display_name: null,
                  email: null,
                  phone: null,
                  address_line1: 'Box 1215',
                  address_line2: null,
                  postal_code: '171 23',
                  city: 'Solna',
                  country: 'SE',
                  provenance: 'predefined',
                  privacy_class: classifyParty({
                    id_type: 'org_no',
                    id_number: '8025562797',
                  }).privacy_class,
                });
                reset();
              }}
              className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50"
            >
              Hyresbostadsägarna?
            </button>
          )}
        </div>
      ) : mode === 'search' ? (
        <div className="space-y-2 rounded border border-stone-200 bg-stone-50 p-3">
          {isSelfPartyable && (
            <div className="text-[10px] font-medium uppercase tracking-widest text-stone-400">
              Er identitet
            </div>
          )}
          <EntityCombobox
            placeholder={
              isSelfPartyable
                ? 'Välj er identitet eller sök i Bolagsverket…'
                : 'Sök i Bolagsverket, förvalda och egna identiteter…'
            }
            onSearch={search}
            onPick={(opt) => pickFromSearch(opt.value)}
            emptyHint={
              isSelfPartyable
                ? 'Inga sparade identiteter. Lägg till en under Inställningar, eller skriv in manuellt.'
                : 'Inga träffar. Lägg till manuellt istället.'
            }
            autoFocus={isSelfPartyable}
            defaultOpen={isSelfPartyable}
          />
          <div className="flex justify-between">
            <button
              type="button"
              onClick={() => setMode('manual')}
              className="rounded px-3 py-1 text-xs font-medium text-stone-600 hover:text-stone-900"
            >
              Skriv in manuellt
            </button>
            {!isSelfPartyable && (
              <button
                type="button"
                onClick={reset}
                className="rounded px-3 py-1 text-xs font-medium text-stone-600"
              >
                Avbryt
              </button>
            )}
          </div>
        </div>
      ) : (
        <PartyDraftForm
          draft={draft}
          onChange={setDraft}
          submitLabel="Lägg till"
          onSubmit={submitManual}
          onCancel={reset}
        />
      )}
    </div>
  );
}

/** One-line contact + address summary under a seated party. */
function PartyContactSummary({ party }: { party: LocalParty }) {
  const address = [
    party.address_line1,
    party.address_line2,
    [party.postal_code, party.city].filter(Boolean).join(' '),
  ]
    .map((s) => s?.trim())
    .filter(Boolean)
    .join(', ');
  const contact = [party.email, party.phone].filter(Boolean).join(' · ');
  if (!address && !contact) {
    return (
      <div className="text-[11px] text-stone-400">
        Ingen adress eller kontaktuppgift angiven.
      </div>
    );
  }
  return (
    <div className="space-y-0.5 text-[11px] text-stone-600">
      {address && <div>{address}</div>}
      {contact && <div>{contact}</div>}
    </div>
  );
}

const inputCls = 'w-full rounded border border-stone-300 bg-white px-2 py-1.5 text-sm';

/**
 * Party add/edit form: identity, name, contact and structured address. An
 * org number triggers a debounced Bolagsverket lookup that fills the
 * fields still empty (name, contact, address) — what the user typed wins.
 */
export function PartyDraftForm({
  draft,
  onChange,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  draft: PartyDraft;
  onChange: (next: PartyDraft) => void;
  submitLabel: string;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const [orgLookup, setOrgLookup] = useState<
    | { state: 'idle' }
    | { state: 'loading'; orgNo: string }
    | { state: 'hit'; orgNo: string; name: string }
    | { state: 'miss'; orgNo: string }
  >({ state: 'idle' });
  const lookupSeq = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  // The lookup only runs when the user changes the org number, not for
  // the number a party was opened for editing with.
  const initialIdNumber = useRef(draft.id_number);

  const set = (patch: Partial<PartyDraft>) => onChange({ ...draft, ...patch });

  const classification = classifyParty({
    id_type: draft.id_type as ClassifierIdType,
    id_number: draft.id_number,
  });

  useEffect(() => {
    if (draft.id_type !== 'org_no') {
      setOrgLookup({ state: 'idle' });
      return;
    }
    const orgNo = draft.id_number.trim();
    // lookupByOrgNo requires min 8 chars (matches backend Zod schema).
    if (
      orgNo.replace(/[\s-]/g, '').length < 8 ||
      orgNo === initialIdNumber.current.trim()
    ) {
      setOrgLookup({ state: 'idle' });
      return;
    }
    const seq = ++lookupSeq.current;
    setOrgLookup({ state: 'loading', orgNo });
    const timer = setTimeout(() => {
      void tenantApi.parties
        .lookupByOrgNo({ org_no: orgNo })
        .then((r) => {
          if (lookupSeq.current !== seq) return;
          if (!r.hit) {
            setOrgLookup({ state: 'miss', orgNo });
            return;
          }
          setOrgLookup({ state: 'hit', orgNo, name: r.hit.legalName });
          onChange(fillDraftFromLookup(draftRef.current, r.hit));
        })
        .catch(() => {
          if (lookupSeq.current !== seq) return;
          setOrgLookup({ state: 'idle' });
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id_type, draft.id_number]);

  return (
    <div className="space-y-2 rounded border border-stone-200 bg-stone-50 p-3">
      <div>
        <label className="block text-[10px] font-medium uppercase tracking-widest text-stone-500">
          ID-typ
        </label>
        <select
          value={draft.id_type}
          onChange={(e) => set({ id_type: e.target.value as PartyIdType })}
          className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 text-sm"
        >
          {(Object.keys(ID_TYPE_LABEL) as PartyIdType[]).map((t) => (
            <option key={t} value={t}>
              {ID_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>
      <input
        value={draft.id_number}
        onChange={(e) => set({ id_number: e.target.value })}
        aria-label="ID-nummer"
        placeholder={
          draft.id_type === 'person_no'
            ? '19850412-1234'
            : draft.id_type === 'org_no'
              ? '556789-1234'
              : 'ID-nummer'
        }
        className={inputCls}
      />
      <div className="flex items-center gap-2 text-[11px] text-stone-500">
        Klassificering: <PartyClassBadge cls={classification.privacy_class} />
        {classification.org_subtype && (
          <span className="text-stone-400">· {classification.org_subtype}</span>
        )}
      </div>
      {draft.id_type === 'org_no' && orgLookup.state !== 'idle' && (
        <div className="text-[11px]">
          {orgLookup.state === 'loading' && (
            <span className="text-stone-500">Söker hos Bolagsverket…</span>
          )}
          {orgLookup.state === 'hit' && (
            <span className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-1.5 py-0.5 font-medium text-green-800">
              <ShieldCheck className="h-3 w-3" />
              Hittat hos Bolagsverket: {orgLookup.name}
            </span>
          )}
          {orgLookup.state === 'miss' && (
            <span className="text-stone-500">
              Ingen träff hos Bolagsverket. Fyll i uppgifterna manuellt.
            </span>
          )}
        </div>
      )}

      <input
        value={draft.legal_name}
        onChange={(e) => set({ legal_name: e.target.value })}
        aria-label="Juridiskt namn"
        placeholder="Juridiskt namn"
        className={inputCls}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          value={draft.email}
          onChange={(e) => set({ email: e.target.value })}
          aria-label="E-post"
          placeholder="E-post (kontakt)"
          className={inputCls}
        />
        <input
          value={draft.phone}
          onChange={(e) => set({ phone: e.target.value })}
          aria-label="Telefon"
          placeholder="Telefon (valfritt)"
          className={inputCls}
        />
      </div>
      <input
        value={draft.address_line1}
        onChange={(e) => set({ address_line1: e.target.value })}
        aria-label="Gatuadress"
        placeholder="Gatuadress"
        className={inputCls}
      />
      <input
        value={draft.address_line2}
        onChange={(e) => set({ address_line2: e.target.value })}
        aria-label="c/o eller adressrad 2"
        placeholder="c/o eller adressrad 2 (valfritt)"
        className={inputCls}
      />
      <div className="grid grid-cols-[8rem_1fr_4rem] gap-2">
        <input
          value={draft.postal_code}
          onChange={(e) => set({ postal_code: e.target.value })}
          aria-label="Postnummer"
          placeholder="Postnummer"
          className={inputCls}
        />
        <input
          value={draft.city}
          onChange={(e) => set({ city: e.target.value })}
          aria-label="Ort"
          placeholder="Ort"
          className={inputCls}
        />
        <input
          value={draft.country}
          onChange={(e) => set({ country: e.target.value.toUpperCase().slice(0, 2) })}
          aria-label="Land"
          placeholder="SE"
          maxLength={2}
          className={inputCls}
        />
      </div>
      <p className="text-[11px] text-stone-500">
        Kontaktuppgift till parten. E-postadress för signering anges per
        signerare i signeringssteget.
      </p>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1 text-xs font-medium text-stone-600"
        >
          Avbryt
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={draft.legal_name.trim().length === 0}
          className="rounded bg-stone-900 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function PrefilledFields({
  fields,
  values,
  partyName,
  onFieldChange,
  disabled,
}: {
  fields: ReadonlyArray<VariableField<Record<string, unknown>>>;
  values: Record<string, unknown>;
  partyName: string;
  onFieldChange: (key: string, value: unknown) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 border-t border-stone-100 pt-3">
      <div className="text-[10px] uppercase tracking-widest text-stone-400">
        Förifyllt från {partyName}
      </div>
      {fields.map((f) => {
        const Renderer = RENDERERS[f.type];
        return (
          <div key={f.key} data-locate-anchor={locateAnchor('field', f.key)}>
            <Renderer
              field={f}
              value={values[f.key]}
              onChange={(next) => onFieldChange(f.key, next)}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Off-slot row (parties not bound to a declared slot)
// ──────────────────────────────────────────────────────────────────────

function OffSlotRow({
  party,
  onRemoveParty,
  disabled,
}: {
  party: LocalParty;
  onRemoveParty: (clientId: string) => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-start justify-between gap-2 rounded border border-stone-200 bg-white px-2 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{party.legal_name}</span>
          <PartyClassBadge cls={party.privacy_class} />
          {!party._persistedId && (
            <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-800">
              Osparad
            </span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-stone-500">
          {ROLE_LABEL[party.role]} · {ID_TYPE_LABEL[party.id_type]}
          {party.id_number ? ` · ${party.id_number}` : ''}
        </div>
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={() => onRemoveParty(party._clientId)}
          className="shrink-0 text-stone-400 hover:text-red-700"
          aria-label="Ta bort"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Class chip
// ──────────────────────────────────────────────────────────────────────

function PartyClassBadge({ cls }: { cls: PartyPrivacyClass }) {
  const Icon = cls === 'searchable_org' ? ShieldCheck : ShieldAlert;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider ${PRIVACY_CLASS[cls]}`}
    >
      <Icon className="h-3 w-3" />
      {PRIVACY_LABEL[cls]}
    </span>
  );
}
